import { CSSProperties } from 'react';
import { CustomMessageBar, ItemList, TConfirmDialogState, TDisplayItem } from '@shared/components';

import { EBulkLiteraturesImportItemStatus, TBulkLiteraturesImport, TBulkLiteraturesImportResult } from '../../types';

type TBuildArgs = {
  job: TBulkLiteraturesImport;
  onDismiss: () => void;
};

// Per-section scroll: each bucket's filename list scrolls internally, so section titles
// (MessageBar headers) stay visible even when the first bucket is long.
const ITEMS_MAX_HEIGHT: CSSProperties = { maxHeight: '180px', overflowY: 'auto' };

const ScrollableItems = ({ items }: { items: TDisplayItem[] }) => (
  <div style={ITEMS_MAX_HEIGHT}>
    <ItemList displayItems={items} isSideBar={false} />
  </div>
);

const rowLabel = (r: TBulkLiteraturesImportResult): string => r.originalname ?? `Row ${r.row}`;

const toItem = (r: TBulkLiteraturesImportResult, extras: { text?: string; href?: string } = {}): TDisplayItem => ({
  id: rowLabel(r),
  text: extras.text ?? rowLabel(r),
  href: extras.href,
});

const buildImportResultDialog = ({ job, onDismiss }: TBuildArgs): TConfirmDialogState | undefined => {
  const created = job.results.filter((r) => r.status === EBulkLiteraturesImportItemStatus.CREATED);
  const allDuplicates = job.results.filter((r) => r.status === EBulkLiteraturesImportItemStatus.DUPLICATE);
  // Duplicates split into two buckets for clarity: ones where we added an assessment to the existing
  // record vs. ones we left entirely alone.
  const duplicatesWithNewAssessment = allDuplicates.filter((r) => (r.addedAssessmentProductIds?.length ?? 0) > 0);
  const duplicatesUnchanged = allDuplicates.filter((r) => (r.addedAssessmentProductIds?.length ?? 0) === 0);
  const noIdentifier = job.results.filter((r) => r.status === EBulkLiteraturesImportItemStatus.FAILED_NO_IDENTIFIER);
  const lookupFailed = job.results.filter((r) => r.status === EBulkLiteraturesImportItemStatus.FAILED_LOOKUP);
  const otherFailed = job.results.filter((r) => r.status === EBulkLiteraturesImportItemStatus.FAILED_OTHER);
  if (allDuplicates.length + noIdentifier.length + lookupFailed.length + otherFailed.length === 0) return undefined;

  const state: TConfirmDialogState = {
    isOpen: true,
    title: 'Import results',
    buttonLabel: 'OK',
    secondaryButtonLabel: 'Close',
    content: (
      <div className="space-y-3">
        {created.length > 0 && (
          <CustomMessageBar intent="success" body={`Imported ${created.length} of ${job.results.length}.`} />
        )}
        {duplicatesWithNewAssessment.length > 0 && (
          <CustomMessageBar
            intent="success"
            multi
            title="Already exists, assessment added"
            body="Click to view existing."
          >
            <ScrollableItems
              items={duplicatesWithNewAssessment.map((r) =>
                toItem(r, { href: r.literatureId ? `/literature/${r.literatureId}` : undefined })
              )}
            />
          </CustomMessageBar>
        )}
        {duplicatesUnchanged.length > 0 && (
          <CustomMessageBar intent="warning" multi title="Already exists" body="Click to view existing.">
            <ScrollableItems
              items={duplicatesUnchanged.map((r) =>
                toItem(r, { href: r.literatureId ? `/literature/${r.literatureId}` : undefined })
              )}
            />
          </CustomMessageBar>
        )}
        {noIdentifier.length > 0 && (
          <CustomMessageBar
            intent="warning"
            multi
            title="No identifier found"
            body="We couldn't find a DOI or PMID for these."
          >
            <ScrollableItems items={noIdentifier.map((r) => toItem(r))} />
          </CustomMessageBar>
        )}
        {lookupFailed.length > 0 && (
          <CustomMessageBar
            intent="warning"
            multi
            title="Lookup failed"
            body="We couldn't find a match in public databases."
          >
            <ScrollableItems
              items={lookupFailed.map((r) => toItem(r, { text: r.doi ? `${rowLabel(r)} (${r.doi})` : rowLabel(r) }))}
            />
          </CustomMessageBar>
        )}
        {otherFailed.length > 0 && (
          <CustomMessageBar
            intent="error"
            multi
            title="Unexpected error"
            body="Something went wrong. Try again or contact support."
          >
            <ScrollableItems items={otherFailed.map((r) => toItem(r))} />
          </CustomMessageBar>
        )}
      </div>
    ),
    onConfirm: onDismiss,
  };
  return state;
};

export { buildImportResultDialog };
